import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { BrowserGrid } from '@/components/shared/browserGrid';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { bigQueryLocations } from '@/data/inventory/regions';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreBigQueryState } from '../restoreBigQueryWizard';

const locationOptions = Object.entries(bigQueryLocations).map(([value]) => ({
  value,
  label: value,
}));

const datasetIdConfig = getCloudFieldConfig(Provider.Gcp, 'bigQueryDatasetId', {
  required: true,
});

export const BigQueryConfiguration = (
  props: StepProps<RestoreBigQueryState>
) => {
  const {
    wizardState: stepState,
    setWizardState,
    stepperLabels,
    back,
    finish,
  } = props;
  const {
    restoreAccount,
    snapshot,
    resourceId,
    targetDatasetId,
    targetLocation,
    availableTables,
    selectedTables,
    isNativePitr,
    restoreToTime,
  } = stepState;

  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [tableMode, setTableMode] = useState<'all' | 'selected'>(
    selectedTables.length === availableTables.length ? 'all' : 'selected'
  );

  const onNextClick = async () => {
    setError(undefined);
    setIsRestoring(true);

    const tablesToRestore = tableMode === 'all' ? undefined : selectedTables;

    if (!restoreAccount) {
      setError('No restore account selected');
      setIsRestoring(false);
      return;
    }

    const restorePromise =
      isNativePitr || !snapshot
        ? dal.restore.bigQuery.nativePitrRestore(resourceId, {
            restoreAccountId: restoreAccount.id,
            destination: {
              datasetId: targetDatasetId,
              location: targetLocation,
            },
            restoreToTime: restoreToTime ? new Date(restoreToTime) : undefined,
            selectedTables: tablesToRestore,
          })
        : dal.restore.bigQuery.restore(resourceId, snapshot.id, {
            restoreAccountId: restoreAccount.id,
            destination: {
              datasetId: targetDatasetId,
              location: targetLocation,
              selectedTables: tablesToRestore,
            },
          });

    void restorePromise
      .then(() => finish(`Started restore job for BigQuery ${targetDatasetId}`))
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore BigQuery: ${errorMessage}`);
      })
      .finally(() => {
        setIsRestoring(false);
      });
  };

  const canGoNext = () => {
    if (isRestoring) {
      return false;
    }
    const isDatasetIdValid =
      datasetIdConfig.validation.validateInput(targetDatasetId);
    const hasTablesSelected = tableMode === 'all' || selectedTables.length > 0;
    return isDatasetIdValid && hasTablesSelected;
  };

  const tableOptions = availableTables.map((t) => t.name || '');

  const selectedLocationOption =
    locationOptions.find((opt) => opt.value === targetLocation) || null;

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={stepperLabels}
      stepperIndex={stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={canGoNext}
      navigationComponent={
        error && (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={snapshot?.pointInTime}
          restoreToTime={restoreToTime}
          vaultId={snapshot?.vaultId}
          accessDeniedItemsCount={snapshot?.accessDeniedItemsCount}
          supportPiTR={isNativePitr}
          backupVaultInfo={{
            cloudProvider: Provider.Gcp,
            region: stepState.sourceRegion,
          }}
        />
      }
    >
      <Box className='mx-[40px] mb-[24px] mt-[44px]'>
        <BrowserGrid gap='36px'>
          <FormControl size='small'>
            <FormLabel>Restore granularity</FormLabel>
            <ToggleButtonGroup
              value={tableMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  if (newMode === 'all') {
                    setTableMode('all');
                    setWizardState((state) => ({
                      ...state,
                      selectedTables: tableOptions,
                    }));
                  } else {
                    setTableMode('selected');
                  }
                }
              }}
              size='small'
              fullWidth
            >
              <ToggleButton value='all'>All tables</ToggleButton>
              <ToggleButton value='selected'>Selected tables</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>

          {tableMode === 'selected' && (
            <FormControl size='small'>
              <FormLabel>Tables to restore</FormLabel>
              <Autocomplete
                data-testid='tablesSelect'
                multiple
                disableCloseOnSelect
                size='small'
                options={tableOptions}
                value={tableOptions.filter((opt) =>
                  selectedTables.includes(opt)
                )}
                onChange={(_, newValue) =>
                  setWizardState((state) => ({
                    ...state,
                    selectedTables: newValue,
                  }))
                }
                renderOption={(optionProps, option, { selected }) => {
                  const { key, ...otherProps } = optionProps;
                  return (
                    <li key={key} {...otherProps}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selectedTables.length ? '' : 'Select tables'}
                    size='small'
                  />
                )}
              />
            </FormControl>
          )}

          <FormTextField
            title='Destination Dataset ID'
            validation={datasetIdConfig.validation}
            placeholder={datasetIdConfig.placeholder}
            value={targetDatasetId}
            onChange={(e) =>
              setWizardState((state) => ({
                ...state,
                targetDatasetId: e.target.value,
              }))
            }
          />

          <FormControl size='small'>
            <FormLabel>Location</FormLabel>
            <Autocomplete
              data-testid='targetLocationSelect'
              size='small'
              options={locationOptions}
              value={selectedLocationOption}
              onChange={(_, newValue) =>
                setWizardState((state) => ({
                  ...state,
                  targetLocation: newValue?.value || '',
                }))
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextField {...params} placeholder='Select location' />
              )}
            />
          </FormControl>
        </BrowserGrid>
      </Box>
    </StepContainer>
  );
};
